import { Controller } from "@hotwired/stimulus"
import { debug, debugWarn } from "../debug"

export default class extends Controller {
  static targets = [ "feedback", "textarea", "emoji", "boldButton" ]

  activeTextarea = null // Property to store the last focused textarea

  connect() {
    debug("✅ Emoji toolbar controller connected to:", this.element);
    debug("Debug info:");
    debug("- Has feedback target:", this.hasFeedbackTarget);
    debug("- Has textarea targets:", this.hasTextareaTarget, "Count:", this.hasTextareaTarget ? this.textareaTargets.length : 0);
    debug("- Has emoji targets:", this.hasEmojiTarget, "Count:", this.hasEmojiTarget ? this.emojiTargets.length : 0);
    debug("- Has bold button target:", this.hasBoldButtonTarget);
    
    // Display debug info in the UI if possible
    if (document.getElementById('emoji-toolbar-debug')) {
      document.getElementById('emoji-toolbar-debug').textContent = "Status: Controller connected";
    }
    
    // Ensure we know the initial focus if a textarea is focused on load
    if (this.hasTextareaTarget) {
      this.textareaTargets.forEach(ta => {
        debug("Found textarea:", ta);
        if (document.activeElement === ta) {
          this.activeTextarea = ta;
          debug("Initial focus tracked: ", ta.id || "unnamed textarea");
        }
        
        // Add focus event listeners to each textarea
        ta.addEventListener('focus', this.handleFocus.bind(this));
        debug("Added focus listener to textarea");
      });
    } else {
      debugWarn("⚠️ No textarea targets found for emoji toolbar");
    }
    
    // Directly bind click handlers to emoji elements if they exist
    if (this.hasEmojiTarget) {
      this.emojiTargets.forEach((emoji, index) => {
        debug(`Setting up emoji ${index}:`, emoji.textContent);
        
        // Make the emoji visually clickable
        emoji.style.cursor = 'pointer';
        
        // Add a direct click listener in addition to the data-action
        emoji.addEventListener('click', (event) => {
          debug("Direct click handler for emoji:", emoji.textContent);
          this.copy(event);
        });
      });
    } else {
      debugWarn("⚠️ No emoji targets found for emoji toolbar");
    }
    
    // Add a direct click listener to the bold button
    if (this.hasBoldButtonTarget) {
      this.boldButtonTarget.addEventListener('click', (event) => {
        debug("Direct click handler for bold button");
        this.wrapStrong(event);
      });
    }
  }
  
  handleFocus(event) {
    this.activeTextarea = event.target;
    debug("Focus event tracked for textarea: ", this.activeTextarea.id || "unnamed textarea");
  }

  copy(event) {
    debug("📋 Copy method called");
    event.preventDefault(); // Prevent any default button behavior
    
    const emoji = event.currentTarget.textContent;
    debug("- Emoji to copy:", emoji);
    
    // Show immediate visual feedback
    this.showFeedback(`Copying: ${emoji}...`);
    
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(emoji).then(() => {
        debug("- Clipboard API succeeded");
        this.showFeedback(`Copied: ${emoji}`);
        
        // If we have an active textarea, also insert the emoji at cursor position
        if (this.activeTextarea) {
          debug("- Inserting into active textarea:", this.activeTextarea);
          const cursorPos = this.activeTextarea.selectionStart;
          const text = this.activeTextarea.value;
          const newText = text.substring(0, cursorPos) + emoji + text.substring(cursorPos);
          this.activeTextarea.value = newText;
          
          // Update cursor position after insertion
          this.activeTextarea.selectionStart = cursorPos + emoji.length;
          this.activeTextarea.selectionEnd = cursorPos + emoji.length;
          this.activeTextarea.focus();
          
          // Trigger input event to notify other controllers of the change
          this.activeTextarea.dispatchEvent(new Event('input', { bubbles: true }));
        } else {
          debugWarn("⚠️ No active textarea found");
        }
      }).catch(err => {
        console.error('Failed to copy emoji:', err);
        this.showFeedback('Copy failed! ' + err.message);
      });
    } else {
      console.error('Clipboard API not available:', !navigator.clipboard ? 'navigator.clipboard missing' : 'writeText method missing');
      this.showFeedback('Clipboard unavailable!');
      
      // Fallback - if clipboard API is not available but we have an active textarea,
      // still insert the emoji at cursor position
      if (this.activeTextarea) {
        debug("- Inserting into active textarea (fallback):", this.activeTextarea);
        const cursorPos = this.activeTextarea.selectionStart;
        const text = this.activeTextarea.value;
        const newText = text.substring(0, cursorPos) + emoji + text.substring(cursorPos);
        this.activeTextarea.value = newText;
        
        // Update cursor position after insertion
        this.activeTextarea.selectionStart = cursorPos + emoji.length;
        this.activeTextarea.selectionEnd = cursorPos + emoji.length;
        this.activeTextarea.focus();
        
        // Trigger input event to notify other controllers of the change
        this.activeTextarea.dispatchEvent(new Event('input', { bubbles: true }));
        
        this.showFeedback(`Inserted: ${emoji}`);
      } else {
        debugWarn("⚠️ No active textarea found for fallback");
      }
    }
  }

  wrapStrong(event) {
    debug("📝 wrapStrong called");
    event.preventDefault();

    const targetTextarea = this.activeTextarea;
    
    if (targetTextarea) {
      debug("- Using active textarea:", targetTextarea);
      const start = targetTextarea.selectionStart;
      const end = targetTextarea.selectionEnd;
      
      debug("- Selection range:", start, "to", end);
      
      if (start === end) {
        this.showFeedback('Select text first!');
        debugWarn("⚠️ No text selected");
        return; // No text selected
      }

      const selectedText = targetTextarea.value.substring(start, end);
      const beforeText = targetTextarea.value.substring(0, start);
      const afterText = targetTextarea.value.substring(end);

      debug("- Selected text:", selectedText);
      
      // Apply bold formatting
      targetTextarea.value = `${beforeText}<strong>${selectedText}</strong>${afterText}`;

      // Set focus back to textarea and position cursor after the newly formatted text
      targetTextarea.focus();
      const newCursorPos = start + 8 + selectedText.length + 9; // 8 for <strong>, 9 for </strong>
      targetTextarea.setSelectionRange(newCursorPos, newCursorPos);

      this.showFeedback('Wrapped in <strong>');
      debug("- Text successfully wrapped in <strong> tags");
      
      // Trigger input event to notify other controllers of the change
      targetTextarea.dispatchEvent(new Event('input', { bubbles: true }));
    } else {
      this.showFeedback('Focus a text area first!');
      console.error("❌ No active textarea found for bold operation");
    }
  }

  showFeedback(message) {
    debug(`Feedback: "${message}"`);
    
    if (this.hasFeedbackTarget) {
      this.feedbackTarget.textContent = message;
      this.feedbackTarget.classList.add('active');
      
      setTimeout(() => {
        if (this.hasFeedbackTarget) { 
          this.feedbackTarget.textContent = '';
          this.feedbackTarget.classList.remove('active');
        }
      }, 2000);
    } else {
      debugWarn("⚠️ No feedback target found");
    }
  }
}